import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {getIcrByNumber as getICRFromTest} from './_test_data/ICRequests.js';
import {
    approveIcr,
    deleteIcrByNumber,
    rejectIcr,
    resolveIcrNumber,
    updateIcr,
} from '../../firebase/firestore/icr';
import {useIcrDocumentSubscription} from '../../firebase/firestore/useIcrDocumentSubscription';
import {getUserDoc} from '../../firebase/firestore/users';
import {useSession} from '../../firebase/UserProvider';
import AuthorChip from '../products/components/common/AuthorChip';
import {toAvatarProfile, userDisplayName} from '../collection/components/PersonCell';
import IcrRightPanel from './components/IcrRightPanel';
import {
    buildPirSavePayload,
    omitUnchangedEeiLinkFields,
    resolveIcrFormEeiState,
    resolvePirEeis,
    resolvePirQuestion,
    resolvePirTimeframe,
    resolvePirTitle,
    resolveIcrPriority,
} from './pirPayload';
import {buildPirDetailCoverage} from './pirCoverageDisplay';
import {useIcrCoverage} from './coverage/useIcrCoverage';
import {
    buildCollectionCoverageView,
    buildCollectionEeiRows,
    buildCollectionEeiSegments,
} from './coverage/buildCollectionCoverageView';
import {resolveDataHeadlinePct} from './coverage/coverageHeadlines';
import IcrCoverageTab from './components/IcrCoverageTab';
import EeiListInput from './components/EeiListInput';
import IcrSimilaritySection from './similarity/IcrSimilaritySection';
import {useIcrSimilarity} from './similarity/useIcrSimilarity';
import EntityTagsSection from './tags/EntityTagsSection';
import EntityTargetsInput from './components/EntityTargetsInput';
import RequirementTargetsSection from './components/RequirementTargetsSection';
import RequirementCollectionPanel from './components/RequirementCollectionPanel';
import EeiPipelineRows from './components/EeiPipelineRows';
import IcrHeaderMeta from './components/IcrHeaderMeta';
import {normalizeIcrClassificationForEdit} from './icrHeaderFormat';
import {buildSortedIcrEeiRows} from './components/EeiCoverageStatusBadges';
import {useRequirementCollectionWork} from './useRequirementCollectionWork';
import {EEI_PRIORITIES} from '../../firebase/firestore/eei';
import {canAccessCollectionTab} from '../collection/collectionTaskPayload';

function sessionAsAvatarProfile(user) {
    if (!user) return null;
    return {
        displayName: user.displayName || user.name || user.email || null,
        hasAvatar: user.hasAvatar === true,
        profilePicture: user.profilePicture || user.photoURL || undefined,
    };
}

function TabLabel({label, count}) {
    return (
        <Stack direction="row" spacing={0} alignItems="center" component="span">
            <span>{label}</span>
            {count != null && (
                <Box
                    component="span"
                    sx={{
                        ml: 0.75,
                        px: 0.8,
                        borderRadius: '10px',
                        bgcolor: 'action.selected',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        lineHeight: 1.8,
                        color: 'text.secondary',
                        minWidth: '1.6em',
                        display: 'inline-block',
                        textAlign: 'center',
                    }}
                >
                    {count}
                </Box>
            )}
        </Stack>
    );
}

const AUTHORIZED_EDIT_ROLES = ['admin', 'collection_manager', 'collector'];
const CLASSIFICATION_OPTIONS = ['UNCLASSIFIED', 'SECRET', 'TOP SECRET'];

function toDateInput(value) {
    if (!value) return '';
    return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
}

const ChipInput = ({label, value = [], onChange}) => {
    const [input, setInput] = useState('');
    const items = Array.isArray(value) ? value : [];
    const add = () => {
        const t = input.trim();
        if (!t) return;
        onChange(Array.from(new Set([...items, t])));
        setInput('');
    };
    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
                <TextField size="small" fullWidth label={label} value={input}
                           onChange={(e) => setInput(e.target.value)}
                           onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                   e.preventDefault();
                                   add();
                               }
                           }}/>
                <Button onClick={add} variant="outlined" size="small">Add</Button>
                {items.length > 0 && <Button onClick={() => onChange([])} variant="text" size="small">Clear</Button>}
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
                {items.map((t) => <Chip key={t} label={t} onDelete={() => onChange(items.filter((x) => x !== t))}/>)}
            </Stack>
        </Stack>
    );
};

const Row = ({label, children}) => (
    <Stack spacing={1.25}>
        <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
        {children}
    </Stack>
);

const Chips = ({items}) => (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
        {(items || []).map((t) => <Chip key={t} label={t} size="small" variant="outlined"/>)}
    </Stack>
);

const ReadingText = ({children, color = 'text.primary'}) => (
    <Typography variant="body1" color={color} sx={{lineHeight: 1.75, maxWidth: '84ch'}}>
        {children || '-'}
    </Typography>
);

const icrToForm = (icr) => {
    const {eeiIds, selectedEeis} = resolveIcrFormEeiState(icr);
    const resolved = resolvePirEeis(icr);
    const tf = resolvePirTimeframe(icr);
    return {
        title: resolvePirTitle(icr),
        question: resolvePirQuestion(icr),
        supportedDecision: icr.supportedDecision || '',
        priority: resolveIcrPriority(icr),
        owner: icr.owner || '',
        timeframeStart: toDateInput(tf.start),
        timeframeDue: toDateInput(tf.due),
        areaOfInterest: icr.areaOfInterest || '',
        classification: normalizeIcrClassificationForEdit(icr.classification),
        tags: Array.isArray(icr.tags) ? icr.tags : [],
        targets: Array.isArray(icr.targets) ? icr.targets : [],
        eeiIds,
        selectedEeis,
        newEeis: [],
        gapIds: [],
        pendingGaps: [],
        manageEeis: true,
        currentAssessment: icr.currentAssessment || '',
        eeis: resolved,
    };
};

const EditICRDialog = ({open, icr, onClose, onSaved, canManageGaps = true}) => {
    const [form, setForm] = useState(() => icrToForm(icr || {}));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const initialFormRef = useRef(null);

    useEffect(() => {
        if (open && icr) {
            const next = icrToForm(icr);
            setForm(next);
            initialFormRef.current = next;
        }
    }, [open, icr]);

    const f = (key) => (e) => setForm((prev) => ({...prev, [key]: e.target.value}));

    const handleSave = async () => {
        if (saving) return;
        if (!icr?.id) {
            setError('Cannot save this ICR — missing document id.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            let payload = buildPirSavePayload(form, {
                existingEeis: resolvePirEeis(icr),
                pirId: resolveIcrNumber(icr),
            });
            payload = omitUnchangedEeiLinkFields(payload, initialFormRef.current, form);
            const updated = await updateIcr(icr.id, payload);
            if (!updated?.id) {
                throw new Error('Save failed — server did not return the updated ICR.');
            }
            onSaved(updated);
            onClose();
        } catch (e) {
            setError(e?.message || 'Failed to save ICR');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle fontWeight={700}>Edit ICR</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5} pt={1}>
                    <Typography variant="subtitle2" color="text.secondary" fontFamily="monospace">
                        {resolveIcrNumber(icr)}
                    </Typography>

                    <TextField fullWidth label="Title" value={form.title} onChange={f('title')}/>
                    <TextField fullWidth multiline minRows={3} label="Question"
                               value={form.question} onChange={f('question')}/>
                    <TextField fullWidth label="Supported Decision"
                               value={form.supportedDecision} onChange={f('supportedDecision')}/>

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                        <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select label="Priority" value={form.priority}
                                    onChange={(e) => setForm((p) => ({...p, priority: e.target.value}))}>
                                {EEI_PRIORITIES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField fullWidth label="Owner" value={form.owner} onChange={f('owner')}/>
                        <FormControl fullWidth>
                            <InputLabel>Classification</InputLabel>
                            <Select label="Classification" value={form.classification}
                                    onChange={(e) => setForm((p) => ({...p, classification: e.target.value}))}>
                                {CLASSIFICATION_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                        <TextField fullWidth type="date" label="Timeframe Start" InputLabelProps={{shrink: true}}
                                   value={form.timeframeStart} onChange={f('timeframeStart')}/>
                        <TextField fullWidth type="date" label="Timeframe Due" InputLabelProps={{shrink: true}}
                                   value={form.timeframeDue} onChange={f('timeframeDue')}/>
                    </Stack>

                    <TextField fullWidth label="Area of Interest"
                               value={form.areaOfInterest} onChange={f('areaOfInterest')}/>
                    <TextField fullWidth multiline minRows={2} label="Current Assessment"
                               value={form.currentAssessment} onChange={f('currentAssessment')}/>
                    <ChipInput label="Tags" value={form.tags}
                               onChange={(tags) => setForm((p) => ({...p, tags}))}/>
                    <EntityTargetsInput
                        value={form.targets}
                        onChange={(targets) => setForm((p) => ({...p, targets}))}
                    />

                    <Divider/>
                    <EeiListInput
                        value={{
                            eeiIds: form.eeiIds || [],
                            newEeis: form.newEeis || [],
                            gapIds: form.gapIds || [],
                            pendingGaps: form.pendingGaps || [],
                        }}
                        initialSelected={form.selectedEeis || []}
                        allowFindGap={canManageGaps}
                        onChange={({eeiIds, newEeis, selectedEeis, gapIds, pendingGaps}) => setForm((p) => ({
                            ...p,
                            eeiIds,
                            newEeis,
                            selectedEeis: selectedEeis || p.selectedEeis,
                            gapIds: gapIds || [],
                            pendingGaps: pendingGaps || [],
                        }))}
                    />

                    {error && <Typography variant="body2" color="error">{error}</Typography>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 1.5}}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const IcrPage = () => {
    const {icrNumber} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const backToRequirements = () => navigate(location.state?.returnTo || '/icr');
    const decoded = icrNumber ? decodeURIComponent(icrNumber) : undefined;
    const {
        data: subscribedIcr,
        loading: subscribedLoading,
        error: subscribedError,
    } = useIcrDocumentSubscription(decoded);
    const [icr, setIcr] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const {user, role, roles} = useSession();

    const canEdit = AUTHORIZED_EDIT_ROLES.includes(role) || (roles || []).some((r) => AUTHORIZED_EDIT_ROLES.includes(r));
    const canDelete = (role === 'collection_manager' && user?.uid && icr?.createdBy === user.uid)
        || role === 'admin'
        || (roles || []).includes('admin');
    const canApproveReject = role === 'collection_manager' || role === 'admin'
        || (roles || []).some((r) => r === 'collection_manager' || r === 'admin');
    const canViewSimilarity = canApproveReject;
    const canViewCollectionTab = canAccessCollectionTab(role, roles);
    const [detailTab, setDetailTab] = useState('overview');
    useEffect(() => {
        if (detailTab === 'similarity' && !canViewSimilarity) {
            setDetailTab('overview');
        } else if (detailTab === 'collection' && !canViewCollectionTab) {
            setDetailTab('overview');
        }
    }, [detailTab, canViewSimilarity, canViewCollectionTab]);
    const [workflowBusy, setWorkflowBusy] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    // Single coverage feed shared with the Collection/Analytic Coverage panels (recompute updates Question/EEIs live)
    const coverage = useIcrCoverage(icr?.id || subscribedIcr?.id);
    const coverageSnapshot = coverage.data;
    const collectionView = useMemo(
        () => buildCollectionCoverageView(coverageSnapshot),
        [coverageSnapshot],
    );
    const detailCoverage = useMemo(
        () => (icr ? buildPirDetailCoverage(icr, coverageSnapshot) : null),
        [icr, coverageSnapshot],
    );
    const icrCollectionEeiSegments = useMemo(
        () => buildCollectionEeiSegments(collectionView, resolvePirEeis(icr)),
        [collectionView, icr],
    );
    const collectionEeiRows = useMemo(
        () => (icr ? buildCollectionEeiRows(collectionView, resolvePirEeis(icr)) : []),
        [collectionView, icr],
    );
    const overallCollectionPct = useMemo(
        () => resolveDataHeadlinePct(coverageSnapshot),
        [coverageSnapshot],
    );
    const collectionComputing = Boolean(
        collectionView?.computing
        || coverageSnapshot?.collectionCoverage?.computing
        || coverage.awaitingCollection,
    );
    const analyticComputing = Boolean(
        (coverageSnapshot?.analyticCoverage?.computing ?? coverageSnapshot?.computing)
        || coverage.awaitingAnalytic,
    );
    const sortedIcrEeis = useMemo(
        () => (icr ? buildSortedIcrEeiRows(icr, detailCoverage, icrCollectionEeiSegments) : []),
        [icr, detailCoverage, icrCollectionEeiSegments],
    );
    const similarity = useIcrSimilarity(icr?.id || subscribedIcr?.id, canViewSimilarity);
    const collectionWork = useRequirementCollectionWork('icr', icr?.id || subscribedIcr?.id);
    const similarityItemCount = useMemo(() => {
        if (!similarity.data) return null;
        const icrEdges = similarity.data.edges?.length || 0;
        const eeiEdges = (similarity.data.eeis || []).reduce(
            (sum, b) => sum + (b.edges?.length || 0), 0,
        );
        return icrEdges + eeiEdges;
    }, [similarity.data]);
    const authorUserId = icr?.createdBy || icr?.userId || subscribedIcr?.createdBy || subscribedIcr?.userId || null;
    const [authorProfile, setAuthorProfile] = useState(null);

    useEffect(() => {
        if (!authorUserId) {
            setAuthorProfile(null);
            return undefined;
        }
        if (authorUserId === user?.uid || authorUserId === user?.id) {
            setAuthorProfile(sessionAsAvatarProfile(user));
            return undefined;
        }
        let cancelled = false;
        getUserDoc(authorUserId)
            .then((doc) => {
                if (cancelled || !doc) return;
                setAuthorProfile(toAvatarProfile(doc));
            })
            .catch(() => {
                if (!cancelled) setAuthorProfile(null);
            });
        return () => { cancelled = true; };
    }, [authorUserId, user]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                if (subscribedError) {
                    if (!cancelled) setIcr(decoded ? getICRFromTest(decoded) : null);
                } else if (subscribedLoading) {
                    if (!cancelled) setLoading(true);
                } else if (!cancelled) {
                    setIcr(subscribedIcr || (decoded ? getICRFromTest(decoded) : null));
                }
            } catch (e) {
                if (!cancelled) setIcr(decoded ? getICRFromTest(decoded) : null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [decoded, subscribedIcr, subscribedLoading, subscribedError]);

    if (loading) {
        return <Stack p={2}><Typography>Loading…</Typography></Stack>;
    }

    if (!icr) {
        return (
            <Stack p={2} spacing={2}>
                <Typography variant="h6">ICR not found</Typography>
                <Button variant="contained" onClick={backToRequirements}>Back</Button>
            </Stack>
        );
    }

    const title = resolvePirTitle(icr);
    const tf = resolvePirTimeframe(icr);
    const displayPriority = resolveIcrPriority(icr);
    const createdAt = icr.createdAt || null;
    const formatCreatedDate = (value) => (
        value && dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY HH:mm') : null
    );
    const authorDisplayName = userDisplayName(authorProfile)
        || (icr.owner && icr.owner !== authorUserId ? icr.owner : null)
        || authorUserId
        || null;

    return (
        <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
            <Stack direction={{xs: 'column', lg: 'row'}} spacing={2.5} alignItems="stretch">
                <Paper elevation={0} sx={{
                    flex: 1,
                    p: {xs: 2, sm: 3.5},
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Stack spacing={3}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Button variant="outlined" size="small" onClick={backToRequirements}>
                                Back
                            </Button>
                            <Stack direction="row" spacing={1}>
                                {canApproveReject && icr.workflowStatus === 'Pending Approval' && (
                                    <>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            disabled={workflowBusy}
                                            onClick={async () => {
                                                setWorkflowBusy(true);
                                                try {
                                                    const updated = await approveIcr(icr.id);
                                                    setIcr((prev) => ({...prev, ...updated}));
                                                    const num = updated?.icrNumber;
                                                    if (num && num !== decoded) {
                                                        navigate(`/icr/${encodeURIComponent(num)}`, {replace: true});
                                                    }
                                                } catch (e) {
                                                    console.error('Failed to approve ICR', e);
                                                } finally {
                                                    setWorkflowBusy(false);
                                                }
                                            }}
                                        >
                                            {workflowBusy ? 'Working…' : 'Approve'}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            disabled={workflowBusy}
                                            onClick={() => { setRejectReason(''); setRejectOpen(true); }}
                                        >
                                            Reject
                                        </Button>
                                    </>
                                )}
                                {canEdit && (
                                    <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>
                                        Edit
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button color="error" size="small" variant="outlined"
                                            onClick={() => setDeleteOpen(true)}>
                                        Delete
                                    </Button>
                                )}
                            </Stack>
                        </Stack>

                        <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                               alignItems={{xs: 'flex-start', sm: 'center'}} spacing={1.5}>
                            <Stack spacing={0.5}>
                                <Typography variant="h4" fontWeight={700} sx={{lineHeight: 1.2}}>
                                    {title}
                                </Typography>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.75}
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{color: 'text.secondary'}}
                                >
                                    <Typography variant="body2" color="inherit" component="span">
                                        {resolveIcrNumber(icr)}
                                    </Typography>
                                    {icr.dtg && (
                                        <>
                                            <Typography variant="body2" color="inherit" component="span">•</Typography>
                                            <Typography variant="body2" color="inherit" component="span">
                                                {icr.dtg}
                                            </Typography>
                                        </>
                                    )}
                                    {(authorUserId || icr.owner) && (
                                        <>
                                            <Typography variant="body2" color="inherit" component="span">•</Typography>
                                            {authorUserId ? (
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        minWidth: 0,
                                                        '& .MuiAvatar-root': {
                                                            width: 20,
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                        },
                                                        '& .MuiLink-root': {
                                                            fontSize: '0.875rem',
                                                            color: 'text.secondary',
                                                            textDecoration: 'none',
                                                        },
                                                    }}
                                                >
                                                    <AuthorChip
                                                        userId={authorUserId}
                                                        userDisplayName={authorDisplayName}
                                                        flex={undefined}
                                                        avatarProfile={authorProfile}
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="inherit" component="span">
                                                    {icr.owner}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                    {formatCreatedDate(createdAt) && (
                                        <>
                                            <Typography variant="body2" color="inherit" component="span">•</Typography>
                                            <Typography variant="body2" color="inherit" component="span">
                                                {formatCreatedDate(createdAt)}
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            </Stack>
                            <IcrHeaderMeta
                                classification={icr.classification}
                                priority={displayPriority}
                                dueDate={tf.due}
                                workflowStatus={icr.workflowStatus}
                            />
                        </Stack>

                        <Tabs
                            value={detailTab}
                            onChange={(_, v) => setDetailTab(v)}
                            sx={{minHeight: 36, borderBottom: 1, borderColor: 'divider'}}
                        >
                            <Tab
                                value="overview"
                                label="Overview"
                                sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                            />
                            {canViewCollectionTab && (
                                <Tab
                                    value="collection"
                                    label={
                                        <TabLabel
                                            label="Collection"
                                            count={collectionWork.loading ? null : collectionWork.totalTasks}
                                        />
                                    }
                                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                                />
                            )}
                            <Tab
                                value="coverage"
                                label={
                                    <TabLabel
                                        label="Coverage"
                                        count={sortedIcrEeis.length}
                                    />
                                }
                                sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                            />
                            {canViewSimilarity && (
                                <Tab
                                    value="similarity"
                                    label={
                                        <TabLabel
                                            label="Similarity"
                                            count={similarityItemCount}
                                        />
                                    }
                                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                                />
                            )}
                        </Tabs>

                        {detailTab === 'overview' && (
                            <Stack spacing={3}>
                                <Row label="Question">
                                    <ReadingText>{resolvePirQuestion(icr)}</ReadingText>
                                </Row>

                                <Row label="Current Assessment">
                                    <ReadingText>{icr.currentAssessment}</ReadingText>
                                </Row>

                                <Stack
                                    direction={{xs: 'column', md: 'row'}}
                                    spacing={3}
                                    alignItems="flex-start"
                                >
                                    <Box sx={{flex: 1, minWidth: 0, width: '100%'}}>
                                        <Row label="Area of Interest">
                                            <ReadingText>{icr.areaOfInterest}</ReadingText>
                                        </Row>
                                    </Box>
                                    <Box sx={{flex: 1, minWidth: 0, width: '100%'}}>
                                        <RequirementTargetsSection
                                            value={icr.targets}
                                            canEdit={canEdit && Boolean(icr.id)}
                                            onSave={async (refs) => {
                                                const updated = await updateIcr(icr.id, {targets: refs});
                                                setIcr((prev) => ({
                                                    ...prev,
                                                    ...(updated || {}),
                                                    targets: updated?.targets ?? refs,
                                                    targetsLocked: updated?.targetsLocked ?? true,
                                                }));
                                            }}
                                        />
                                    </Box>
                                </Stack>

                                <Row label={
                                    <Tooltip title="Essential Elements of Information" arrow placement="top">
                                        <span>EEIs</span>
                                    </Tooltip>
                                }>
                                    {sortedIcrEeis.length === 0 ? (
                                        <ReadingText>-</ReadingText>
                                    ) : (
                                        <EeiPipelineRows
                                            rows={sortedIcrEeis}
                                            onNavigateToEei={(eei) => navigate(
                                                `/icr/eei/${encodeURIComponent(eei.eeiNumber || eei.id)}`,
                                            )}
                                        />
                                    )}
                                </Row>

                                <Divider/>
                                {canViewSimilarity && icr.id ? (
                                    <EntityTagsSection
                                        entityType="ICR"
                                        entityId={icr.id}
                                        canEdit={canViewSimilarity}
                                        enabled={canViewSimilarity}
                                    />
                                ) : (icr.tags || []).length > 0 ? (
                                    <Row label="Tags">
                                        <Chips items={icr.tags}/>
                                    </Row>
                                ) : null}
                            </Stack>
                        )}

                        {detailTab === 'coverage' && (
                            <IcrCoverageTab
                                detailCoverage={detailCoverage}
                                collectionView={collectionView}
                                collectionSegments={icrCollectionEeiSegments}
                                collectionEeiRows={collectionEeiRows}
                                overallCollectionPct={overallCollectionPct}
                                coverageSnapshot={coverageSnapshot}
                                collectionComputing={collectionComputing}
                                analyticComputing={analyticComputing}
                                questionText={resolvePirQuestion(icr)}
                                onNavigateToEei={(eei) => navigate(
                                    `/icr/eei/${encodeURIComponent(eei.eeiNumber || eei.id)}`,
                                )}
                            />
                        )}

                        {canViewCollectionTab && detailTab === 'collection' && (
                            <RequirementCollectionPanel
                                tasks={collectionWork.tasks}
                                loading={collectionWork.loading}
                                error={collectionWork.error}
                            />
                        )}

                        {canViewSimilarity && detailTab === 'similarity' && (
                            <IcrSimilaritySection similarity={similarity}/>
                        )}
                    </Stack>
                </Paper>

                <Stack sx={{width: {xs: '100%', lg: 360}, flexShrink: 0}}>
                    <IcrRightPanel
                        icr={icr}
                        role={role}
                        coverage={coverage}
                        onIcrUpdate={(updated) => setIcr((prev) => ({...prev, ...updated}))}
                    />
                </Stack>
            </Stack>

            <EditICRDialog
                open={editOpen}
                icr={icr}
                onClose={() => setEditOpen(false)}
                onSaved={(updated) => setIcr((prev) => ({...prev, ...updated}))}
                canManageGaps={canViewSimilarity}
            />

            <Dialog open={deleteOpen} onClose={() => (deleting ? null : setDeleteOpen(false))} maxWidth="xs" fullWidth>
                <DialogTitle>Delete ICR</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to permanently delete this ICR ({resolveIcrNumber(icr)})?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={deleting} onClick={async () => {
                        if (deleting) return;
                        setDeleting(true);
                        try {
                            const ok = await deleteIcrByNumber(resolveIcrNumber(icr));
                            if (ok) {
                                setDeleteOpen(false);
                                backToRequirements();
                            } else {
                                setDeleteOpen(false);
                            }
                        } catch (e) {
                            console.error('Failed to delete ICR', e);
                        } finally {
                            setDeleting(false);
                        }
                    }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => workflowBusy ? null : setRejectOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Reject ICR</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <Typography variant="body2">
                            Reject this pending ICR proposal?
                        </Typography>
                        <TextField
                            label="Reason (optional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            disabled={workflowBusy}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)} disabled={workflowBusy}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={workflowBusy} onClick={async () => {
                        setWorkflowBusy(true);
                        try {
                            const updated = await rejectIcr(icr.id, {reason: rejectReason.trim() || undefined});
                            setIcr((prev) => ({...prev, ...updated}));
                            setRejectOpen(false);
                        } catch (e) {
                            console.error('Failed to reject ICR', e);
                        } finally {
                            setWorkflowBusy(false);
                        }
                    }}>{workflowBusy ? 'Rejecting…' : 'Reject'}</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default IcrPage;
